import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_PATH } from '@core/constants';
import { TvStartupService } from '@core/services/common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'caremoni-startup-tv',
  templateUrl: './startup-tv.component.html',
  styleUrl: './startup-tv.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class StartupTvComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() autoNavigate = true;
  private readonly startupTimeoutMs = 60000;
  private readonly minimumStartupVisibleMs = 450;
  private readonly subscription = new Subscription();
  private startupVisibleAt = 0;
  isLoading = true;
  errorMessage = '';
  statusMessage = 'ログイン状態を確認しています';

  constructor(
    private readonly router: Router,
    private readonly tvStartupService: TvStartupService
  ) { }

  async ngOnInit(): Promise<void> {
    this.subscription.add(
      this.tvStartupService.statusMessage$.subscribe(message => {
        this.statusMessage = message;
      })
    );
  }

  ngAfterViewInit(): void {
    if (!this.autoNavigate) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        void this.startStartupFlow();
      });
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private async startStartupFlow(): Promise<void> {
    try {
      this.log('ログイン状態を確認しています');
      this.startupVisibleAt = Date.now();
      this.hideNativeSpinner();
      await this.ensureMinimumStartupVisible();

      const navigated = await this.withTimeout(
        this.tvStartupService.navigateToCurrentStep(),
        this.startupTimeoutMs,
        'startup flow'
      );

      if (!navigated) {
        this.log('通常ログイン画面へ移動します');
        await this.withTimeout(
          this.router.navigateByUrl(ROUTE_PATH.CUST_LOGIN, { replaceUrl: true }),
          this.startupTimeoutMs,
          'fallback navigation'
        );
      } else {
        this.log('起動準備が完了しました');
      }
    } catch (error) {
      this.log('起動準備に失敗しました');
      this.isLoading = false;
      this.errorMessage = 'ログイン状態を確認できませんでした。しばらくしてから再度お試しください。';
    }
  }

  private withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error(`TV startup timeout: ${label}`)), ms);
      promise
        .then(resolve)
        .catch(reject)
        .finally(() => window.clearTimeout(timer));
    });
  }

  private async ensureMinimumStartupVisible(): Promise<void> {
    const elapsedMs = Date.now() - this.startupVisibleAt;
    const remainingMs = this.minimumStartupVisibleMs - elapsedMs;
    if (remainingMs <= 0) return;

    await new Promise<void>(resolve => window.setTimeout(resolve, remainingMs));
  }

  private log(message: string): void {
    this.statusMessage = message;
    console.info(`[TV Startup] ${message}`);
  }

  private hideNativeSpinner(): void {
    const androidBridge = (window as any).AndroidBridge;
    if (androidBridge && typeof androidBridge.onSpinnerAppShow === 'function') {
      androidBridge.onSpinnerAppShow(false);
    }
  }
}
